import { useNavigate } from "react-router-dom";
import { useSelector } from "@services/hooks/redux-hooks";
import moment from "moment";

import EsfirusTable from "@components/ui/Table/EsfirusTable"
import { EsfirusTablePropsPagination, HandleChangePaginationType } from "@services/hooks/useHandlerPaginationTable";
import { findUserByCompanyAndEmployeeIds } from "@helpers/utils";
import { NoticeDefinitive } from "@models/notices";
import { Check, EditOutlined, Replay, SwapHoriz } from "@mui/icons-material";
import { useState } from "react";
import { ModalConfirmarOpenClose } from "./ModalConfirmarOpenClose";
import { ModalChangeStatus } from "./ModalChangeStatus";
import { avisosService } from "@services/avisos";

interface ListadoAvisosTableProps {
  avisos: NoticeDefinitive[];
  pagination: EsfirusTablePropsPagination;
  totalItems: number;
  handleChangePagination: HandleChangePaginationType;
  tableId: string;
  loading?: boolean;
  children?: React.ReactNode;
}

const ListadoAvisosDefinitivosTable = ({ avisos, pagination, totalItems, handleChangePagination, tableId, loading, children }: ListadoAvisosTableProps) => {
  const navigate = useNavigate()
  const empresas = useSelector(state => state.empresas.list)
  const users = useSelector(state => state.users.list)
  const noticeStatuses = useSelector(state => state.notices.listStatuses)
  const noticeTypes = useSelector(state => state.notices.listTypes)

  // Gestión de modales.
  const [closeOpenModalData, setCloseOpenModalData] = useState<{ aviso: any, open: boolean }>({ aviso: null, open: false })
  const [isClosingAviso, setIsClosingAviso] = useState<boolean>(false)
  const [changeStatusModalData, setChangeStatusModalData] = useState<{ aviso: any, open: boolean }>({ aviso: null, open: false })
  const [isChangingStatusAviso, setIsChangingStatusAviso] = useState<boolean>(false)

  const handleChangeStatus = async ({ newStatus, comments, sendMailEmployee, sendAlertEmployee, sendMailContacts }: { newStatus: string, comments: string, sendMailEmployee: boolean, sendAlertEmployee: boolean, sendMailContacts: boolean }) => {
    setIsChangingStatusAviso(true)

    try {
      const aviso = changeStatusModalData.aviso
      await avisosService.changeStatus({ unique_id: aviso?.unique_id, new_state: newStatus, new_employee: aviso?.destination_employee, comments, send_mail_employee: sendMailEmployee, send_alert_employee: sendAlertEmployee, send_mail_contacts: sendMailContacts })
      setChangeStatusModalData(prev => ({ ...prev, aviso: null, open: false }))
      window.location.reload()
    } catch (error) {
      console.error(error)
    } finally {
      setIsChangingStatusAviso(false)
    }
  }

  const handleCloseOrOpen = async () => {
    setIsClosingAviso(true)

    try {
      const aviso = closeOpenModalData.aviso
      await avisosService.openClose({ unique_id: aviso?.unique_id, open: !aviso?.est_avis })
      setCloseOpenModalData(prev => ({ ...prev, aviso: null, open: false }))
      window.location.reload()
    } catch (error) {
      console.error(error)
    } finally {
      setIsClosingAviso(false)
    }
  }

  const columns = [
    {
      name: "Empresa - Delegación",
      keymap: "_id",
      see: true,
      order: true,
      width: 150,
      notShowEdit: true,
      showCrearParte: true,
      handleView: (row: any) => navigate(`/avisos/${row.unique_id}`),
      handleCrearParte: (row: any) => navigate({
        pathname: '/partes/nuevo',
        search: `?from_aviso=${row.unique_id}`
      }),
      actions: [
        {
          handle: (row: any) => navigate(`/avisos/${row.unique_id}/edit`),
          icon: () => <EditOutlined sx={{ color: "#59dcf4" }} />,
          title: () => "Modificar aviso",
          disabled: (row: any) => false,
        },
        {
          handle: (row: any) => setChangeStatusModalData(prev => ({ ...prev, aviso: row, open: true })),
          icon: (row: any) => <SwapHoriz color={!row.est_avis ? "disabled" : "warning"} />,
          title: () => "Cambiar estado",
          disabled: (row: any) => !row.est_avis,
        },
        {
          handle: (row: any) => setCloseOpenModalData(prev => ({ ...prev, aviso: row, open: true })),
          icon: (row: any) => row.est_avis ? <Check color="success" /> : <Replay color="success" />,
          title: (row: any) => row.est_avis ? "Cerrar aviso" : "Reabrir aviso",
        }
      ]
    }, {
      name: 'ID de Aviso definitivo',
      keymap: 'id_definitivo',
      order: true,
      width: 100
    }, {
      name: "Fecha",
      keymap: "date",
      parse: (v: any) => moment(v).format("DD-MM-YYYY"),
      order: true,
      width: 100
    }, {
      name: "Hora",
      keymap: "time",
      parse: (v: any) => moment.utc(v, "HHmm").format("HH:mm:ss"),
      order: true,
      width: 50
    }, {
      name: "Cliente",
      keymap: "customer",
      order: true,
      width: 50
    }, {
      name: "Empleado origen",
      keymap: "origin_employee_name",
      order: true,
      width: 100
    }, {
      name: "Empleado destino",
      keymap: "destination_employee_name",
      order: true,
      width: 100
    }, {
      name: "Empleado responsable",
      keymap: "employee_name",
      order: true,
      width: 100
    }, {
      name: "Estado",
      keymap: "status_name",
      order: true,
      width: 100
    }, {
      name: "Tipo",
      keymap: "notification_type_name",
      order: true,
      width: 100
    }, {
      name: "Descripción",
      keymap: "description_r_791",
      order: true,
      width: 150,
      tooltipProperty: "description_tooltip"
    }
  ]

  const parseRows = (rows: NoticeDefinitive[] = []) => {
    return rows.map(row => {
      const empresaName = empresas.find((empresa: any) => empresa.Empresa === row.company)?.Nombre ?? ""
      const empleadoOrigenName = findUserByCompanyAndEmployeeIds(users, row.company, row.origin_employee)
      const empleadoDestinoName = findUserByCompanyAndEmployeeIds(users, row.company, row.destination_employee)
      const empleadoResponsableName = findUserByCompanyAndEmployeeIds(users, row.company, row.employee)
      const estadoName = noticeStatuses.find((estado: any) => estado.status === row.status)?.description ?? ""
      const tipoName = noticeTypes.find((tipo: any) => tipo.notification_type === row.notification_type)?.description ?? ""

      return ({
        _id: `${empresaName} - ${row.branch ?? ""}`,
        id_definitivo: row.notification ?? '-',
        origin_employee_name: `${empleadoOrigenName?.nombre ?? "-"} ${empleadoOrigenName?.apellidos ?? "-"}`,
        destination_employee_name: `${empleadoDestinoName?.nombre ?? "-"} ${empleadoDestinoName?.apellidos ?? "-"}`,
        employee_name: `${empleadoResponsableName?.nombre ?? "-"} ${empleadoResponsableName?.apellidos ?? "-"}`,
        status_name: estadoName,
        notification_type_name: tipoName,
        description_tooltip: `Descripción: ${row.description_r_791 || ""}\nComentarios: ${row.comment || ""}`,
        ...row
      })
    })
  }

  return (
    <>
      <EsfirusTable
        columns={columns}
        rows={parseRows(avisos)}
        rowsCount={totalItems}
        pagination={pagination}
        withPagination={true}
        handleChangePagination={handleChangePagination}
        tableId={tableId}
        loading={loading}
      >
        {children}
      </EsfirusTable>

      {/* Modal para cambiar de estado de aviso */}
      {changeStatusModalData.open && (
        <ModalChangeStatus
          open={changeStatusModalData.open}
          onClose={() => setChangeStatusModalData(prev => ({ ...prev, aviso: null, open: false }))}
          aviso={changeStatusModalData.aviso}
          onClick={handleChangeStatus}
          isLoading={isChangingStatusAviso}
        />
      )}

      {/* Modal para confirmar el cierre o reapertura */}
      {closeOpenModalData.open && (
        <ModalConfirmarOpenClose
          open={closeOpenModalData.open}
          onClose={() => setCloseOpenModalData(prev => ({ ...prev, aviso: null, open: false }))}
          textHeader={`${closeOpenModalData.aviso?.est_avis ? "Cerrar" : "Reabrir"} aviso`}
          description={`¿Estás seguro de que quieres ${closeOpenModalData.aviso?.est_avis ? "cerrar" : "reabrir"} el aviso?`}
          onClick={handleCloseOrOpen}
          isLoading={isClosingAviso}
        />
      )}
    </>
  )
}

export default ListadoAvisosDefinitivosTable